import { observer } from 'mobx-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Box, Button, Chip, Tooltip, Typography } from '@mui/material';
import MediaTypography from 'components/MediaTypography';
import { DateTime } from 'luxon';
import store from 'store';

import { useServiceRouter } from '@common/components/ServiceRouter';
import { ROUTES } from '@common/constants/CampusRoutes';
// import CircleProgress from 'components/CircleProgress';
import { getPluralKey } from '@common/helpers/getPluralKey';
import isDatePassed from '@common/helpers/isDatePassed';
import { IStudentSimpleProgramModel } from '@common/models/StudentPrograms/StudentSimpleProgram';

import {
	ImageWrapper,
	Overlay,
	ProgramItemImage,
	ProgramItemInfo,
	ProgramItemInfoSubTitle,
	ProgramItemInfoTitle,
	ProgramItemLink,
	ProgramItemWrap,
	StyledLockIcon,
	StyledProgramItemDeadline,
} from './style';

interface ProgramItemProps {
	programModel: IStudentSimpleProgramModel;
}

function ProgramItem({ programModel }: ProgramItemProps) {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const [hasError, setHasError] = useState(false);
	const { checkService } = useServiceRouter();
	const [, setIsPurchaseModalOpen] = useState(false);
	const isCreditsTisStudent = store.profile?.isCreditsTisStudentView;

	const { deadline } = programModel;
	const { productType } = programModel;
	const isMentored = productType === 'Mentored';

	const formattedDate = DateTime.fromISO(deadline).toFormat('dd.MM.yyyy');

	const {
		program_id: programId,
		group_id: groupId,
		program_group_id: programGroupId,
		is_blocked: isBlocked,
		program_products: programProducts,
		is_purchasable: isPurchasable,
	} = programModel;

	const isDeadlinePassed = isDatePassed(deadline);

	const Component = !isBlocked ? ProgramItemLink : Box;

	const isTalented = checkService('talented');

	const customTooltip = (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'center',
				alignItems: 'center',
				gap: '8px',
				textAlign: 'center',
			}}
		>
			<MediaTypography variant="subtitle3">{t('talented.orderNotification.title')}</MediaTypography>
			<MediaTypography variant="body3">{t('talented.orderNotification.message')}</MediaTypography>
		</Box>
	);

	const basePath = `/programs/${programId}/${groupId}/${programGroupId}`;
	const link = store.profile.checkRole('Partner Invitee') ? `${basePath}?openTab=0` : basePath;

	const openPurchaseModal = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
		event.stopPropagation();
		setIsPurchaseModalOpen(true);
	};

	return (
		<Component href={link}>
			<Tooltip title={isTalented && !store.profile.consultation ? customTooltip : undefined}>
				<ProgramItemWrap>
					<ImageWrapper hasError={hasError}>
						<ProgramItemImage
							src={programModel.program_cover}
							onError={() => setHasError(true)}
							alt="Program image"
						/>
						{isBlocked && (
							<Overlay hasError={hasError}>
								<StyledLockIcon hasError={hasError} />
							</Overlay>
						)}
					</ImageWrapper>

					<Box display="flex" alignItems="flex-end" gap="4px" justifyContent="space-between">
						<ProgramItemInfo>
							<Box display="flex" alignItems="center" flexWrap="wrap" gap="8px">
								<ProgramItemInfoSubTitle>
									{t(`programs.lessons.${getPluralKey(programModel.total_lessons)}`, {
										count: programModel.total_lessons,
									})}
								</ProgramItemInfoSubTitle>
								{checkService('international') && productType && (
									<Chip
										label={productType}
										size="small"
										variant="outlined"
										sx={{
											height: '24px',
											fontSize: '14px',
											fontWeight: 600,
											borderRadius: '8px',
											color: (theme) => {
												if (!isMentored) return theme.palette.text.primary;
												return theme.palette.mode === 'dark' ? '#FFAA5B' : '#B84A00';
											},
											borderColor: isMentored ? '#EB812F' : 'divider',
											backgroundColor: isMentored ? 'rgba(235, 129, 47, 0.1)' : 'action.selected',
											'& .MuiChip-label': { px: '8px' },
										}}
									/>
								)}
							</Box>
							<ProgramItemInfoTitle variant="h3" lineClamp={{ default: 2 }}>
								{programModel.program_name}
							</ProgramItemInfoTitle>
							{deadline && (
								<StyledProgramItemDeadline
									label={
										<Typography variant="subtitle1">
											{t(isDeadlinePassed ? 'shared.deadlineWas' : 'shared.deadlineUntil', {
												date: formattedDate,
											})}
										</Typography>
									}
									color={isDeadlinePassed ? 'error' : 'info'}
								/>
							)}
						</ProgramItemInfo>
						{/* <CircleProgress progress={programModel.progress} /> */}
						{isBlocked &&
							!isCreditsTisStudent &&
							isPurchasable &&
							(!isTalented ? (
								<Button
									variant="contained"
									sx={{ minWidth: '100px' }}
									onClick={() => navigate(`${ROUTES.MARKET}/${programProducts?.[0]?.id}`)}
								>
									Buy
								</Button>
							) : null)}
						{isBlocked && isPurchasable && isCreditsTisStudent ? (
							<Button variant="contained" sx={{ minWidth: '120px' }} onClick={openPurchaseModal}>
								Buy credit
							</Button>
						) : null}
					</Box>
				</ProgramItemWrap>
			</Tooltip>
		</Component>
	);
}

export default observer(ProgramItem);
